'use client';

import { memo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import { HouseIcons } from '@/assets/icons/HouseIcons';
import NextButton from '@/components/Button/NextButton';
import { FormData } from '@/types/form';
import { fieldRules } from '@/utils/form';
import generalStyles from '../FormBody.module.scss';
import OptionButtons from '../OptionButtons/OptionButtons';
import useModal from '@/hooks/useModal';
import styles from './StepSix.module.scss';

interface Props {
  formData?: FormData;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  register?: UseFormRegister<FormData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
}

const HouseOwnForm = ({
  formData,
  handleSubmit,
  register,
  control,
  setValue,
  trigger,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const [selected, setSelected] = useState(formData?.house_ownership);
  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit?.();
    if (!errors?.house_ownership) {
      closeModal?.();
    }
  };

  const onChange = (
    value: 'rent' | 'own with outright' | 'own with mortgage'
  ) => {
    setSelected(value);
    setValue?.('house_ownership', value);
    trigger?.('house_ownership');
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      <div className={styles.optionsWrapper}>
        {register && (
          <input
            type="text"
            className={generalStyles.hiddenInput}
            id="house_ownership"
            {...register('house_ownership', fieldRules.house_ownership)}
          />
        )}
        <OptionButtons
          icon={<HouseIcons icon="key" />}
          text="I rent"
          selected={selected === 'rent'}
          onClick={() => onChange('rent')}
        />
        <OptionButtons
          icon={<HouseIcons icon="check" />}
          text="Own outright"
          selected={selected === 'own with outright'}
          onClick={() => onChange('own with outright')}
        />
        <OptionButtons
          icon={<HouseIcons icon="dollar" />}
          text="Own with mortgage"
          selected={selected === 'own with mortgage'}
          onClick={() => onChange('own with mortgage')}
        />
        {errors?.house_ownership && (
          <span className={generalStyles.error}>
            {errors.house_ownership.message}
          </span>
        )}
      </div>
      <NextButton
        buttonType="submit"
        type="button"
        text="Apply changes"
        styles={`${generalStyles.button} ${generalStyles.smallButton}`}
      />
    </form>
  );
};

export default memo(HouseOwnForm);
